import React, { useEffect, useState } from "react";
import { Lock, Users, Eye, CalendarCheck, Mail, TrendingUp, RefreshCw } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { API } from "../lib/api";

const Stat = ({ icon: Icon, label, value }) => (
  <div className="card-soft p-6">
    <div className="flex items-center justify-between">
      <span className="text-[10px] uppercase tracking-[0.3em] text-gold">{label}</span>
      <Icon size={18} className="text-gold" />
    </div>
    <p className="font-serif text-4xl text-emerald mt-3">{value ?? "—"}</p>
  </div>
);

export default function AdminAnalytics() {
  useEffect(() => { document.title = "Admin Dashboard — Hazrat Sufi Sarkar"; }, []);
  const [password, setPassword] = useState(sessionStorage.getItem("admin_pw") || "");
  const [authed, setAuthed] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (pw = password) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/analytics/stats?password=${encodeURIComponent(pw)}`);
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || "Failed");
      setData(json);
      sessionStorage.setItem("admin_pw", pw);
      setAuthed(true);
    } catch (e) {
      toast.error(e.message);
      setAuthed(false);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (password) load(password); /* eslint-disable-next-line */ }, []);

  if (!authed) {
    return (
      <div className="min-h-[80vh] pattern-bg flex items-center" data-testid="admin-dashboard-login">
        <div className="container-page max-w-md">
          <div className="card-soft p-10">
            <div className="flex items-center gap-3 mb-6">
              <Lock className="text-gold" size={22} />
              <h1 className="font-serif text-3xl text-emerald">Admin Dashboard</h1>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); load(password); }} className="space-y-5">
              <div>
                <Label className="uppercase tracking-[0.18em] text-xs text-charcoal-muted">Admin Password</Label>
                <Input type="password" value={password} onChange={e => setPassword(e.target.value)} className="mt-2 bg-transparent border-gold/40 focus-visible:ring-gold" data-testid="admin-pw-input" autoFocus />
              </div>
              <button type="submit" disabled={loading} className="btn-gold w-full disabled:opacity-60" data-testid="admin-login-btn">
                {loading ? "Loading..." : "Enter Dashboard"}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  const maxDay = Math.max(1, ...(data?.by_day || []).map(d => d.count));

  return (
    <div className="min-h-[80vh] pattern-bg pb-24" data-testid="admin-dashboard">
      <section className="pattern-emerald text-ivory pt-32 pb-16">
        <div className="container-page flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="text-[11px] uppercase tracking-[0.32em] text-gold">Admin</span>
            <h1 className="font-serif text-4xl md:text-5xl mt-2 leading-tight">Visitor Dashboard</h1>
            <p className="text-ivory/70 mt-3 text-sm">Live counts from the website — refresh anytime.</p>
          </div>
          <button onClick={() => load()} className="btn-outline-gold inline-flex items-center gap-2" data-testid="refresh-btn">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </section>

      <section className="container-page -mt-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="stats-grid">
          <Stat icon={Eye} label="Total Visits" value={data?.total_visits} />
          <Stat icon={Users} label="Unique Visitors" value={data?.unique_sessions} />
          <Stat icon={CalendarCheck} label="Bookings" value={data?.bookings} />
          <Stat icon={Mail} label="Contacts" value={data?.contacts} />
        </div>

        <div className="grid md:grid-cols-2 gap-6 mt-8">
          <div className="card-soft p-6" data-testid="by-day-chart">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-serif text-xl text-emerald">Last 14 days</h3>
              <TrendingUp size={16} className="text-gold" />
            </div>
            <div className="flex items-end justify-between gap-1 h-44">
              {(data?.by_day || []).map(d => (
                <div key={d.day} className="flex-1 flex flex-col items-center gap-2" title={`${d.day}: ${d.count}`}>
                  <div
                    className="w-full bg-gradient-to-t from-emerald to-gold rounded-t"
                    style={{ height: `${(d.count / maxDay) * 100}%`, minHeight: 4 }}
                  />
                  <span className="text-[9px] text-charcoal-muted rotate-45 origin-left whitespace-nowrap">{d.day.slice(5)}</span>
                </div>
              ))}
              {(!data?.by_day || data.by_day.length === 0) && (
                <div className="w-full text-center text-sm text-charcoal-muted">No visits yet — share the site to see data here.</div>
              )}
            </div>
          </div>

          <div className="card-soft p-6" data-testid="by-path-list">
            <h3 className="font-serif text-xl text-emerald mb-5">Most-visited pages</h3>
            <ul className="space-y-3">
              {(data?.by_path || []).slice(0, 10).map(p => (
                <li key={p.path} className="flex items-center justify-between text-sm">
                  <code className="text-emerald font-semibold">{p.path}</code>
                  <span className="text-charcoal-muted">{p.count} visits</span>
                </li>
              ))}
              {(!data?.by_path || data.by_path.length === 0) && (
                <li className="text-charcoal-muted text-sm">No data yet.</li>
              )}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
